import { Pressable, StyleSheet, Text, View } from 'react-native';
import Animated, { FadeInDown, FadeOut } from 'react-native-reanimated';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';
import { useColorScheme } from '@/hooks/use-color-scheme';

interface FABCoachMarkProps {
  visible: boolean;
  /** Distance from screen bottom to the TOP of the FAB (fab bottom + fab height). */
  bottomOffset: number;
  onDismiss: () => void;
}

/**
 * FABCoachMark - one-time tooltip bubble anchored above the center AI FAB,
 * explaining what the unlabeled button does. Tap anywhere on it to dismiss.
 */
export function FABCoachMark({ visible, bottomOffset, onDismiss }: FABCoachMarkProps) {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];

  if (!visible) return null;

  return (
    <Animated.View
      entering={FadeInDown.duration(250)}
      exiting={FadeOut.duration(150)}
      style={[styles.wrapper, { bottom: bottomOffset + Spacing.md }]}
      pointerEvents="box-none"
    >
      <Pressable
        onPress={onDismiss}
        style={[styles.bubble, { backgroundColor: palette.primary }]}
        accessibilityRole="button"
        accessibilityLabel="Dismiss Fim introduction"
      >
        <Text style={[styles.bubbleText, { fontFamily: getBodyFont() }]}>
          Meet Fim — tap to chat or get AI suggestions
        </Text>
      </Pressable>
      <View style={[styles.pointer, { borderTopColor: palette.primary }]} />
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    left: Spacing.xl,
    right: Spacing.xl,
    alignItems: 'center',
    zIndex: 101, // above the FAB (100)
  },
  bubble: {
    maxWidth: 280,
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderRadius: BorderRadius.lg,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 6,
    elevation: 6,
  },
  bubbleText: {
    color: '#FFFFFF',
    fontSize: 14,
    textAlign: 'center',
  },
  pointer: {
    width: 0,
    height: 0,
    borderLeftWidth: 8,
    borderRightWidth: 8,
    borderTopWidth: 8,
    borderLeftColor: 'transparent',
    borderRightColor: 'transparent',
  },
});
